import { ApolloCache, DefaultContext, MutationUpdaterFunction, useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import {
  Button,
  colors,
  ControlledInput,
  Modal,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
} from '@ui-components/neo.tax';
import { ReactComponent as Presentation } from '@ui-components/neo.tax/dist/icons/presentation-projector-screen-hierarchy.svg';
import { ReactElement, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { UPDATE_PRODUCT, Exact, UpdateProductMutationVariables, UpdateProductMutation } from '@neo.tax/shared/graphql';
import { Product } from '@neo.tax/shared/hooks/useProducts';
import { useAnalyticId } from '@neo.tax/shared/state';

type UpdateProductModalProps = {
  product: Product | null;
  onClose: () => void;
  onUpdate: MutationUpdaterFunction<
    UpdateProductMutation,
    Exact<{
      productId: string;
    }>,
    DefaultContext,
    ApolloCache<any>
  >;
};

export const UpdateProductModal = ({ onClose, onUpdate, product }: UpdateProductModalProps): ReactElement => {
  const [updateProduct, { loading }] = useMutation<UpdateProductMutation, UpdateProductMutationVariables>(
    UPDATE_PRODUCT,
  );
  const { control, handleSubmit, reset } = useForm<{ productName: string }>({
    mode: 'onSubmit',
    defaultValues: {
      productName: product?.name ?? '',
    },
  });
  const nameId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_UPDATE_PRODUCT_NAME);

  const handleCancel = (): void => {
    reset({
      productName: '',
    });
    onClose();
  };

  useEffect(() => {
    reset({
      productName: product?.name ?? '',
    });
  }, [product, reset]);

  const onSubmit: SubmitHandler<{ productName: string }> = async (data) => {
    if (!product) return;
    await updateProduct({
      variables: {
        productId: product.id,
        updateProductInput: {
          name: data.productName,
        },
      },
      optimisticResponse: {
        updateProduct: {
          id: product.id,
          __typename: 'Product',
          name: data.productName,
        },
      },
      update: (cache, { data }, context) => {
        if (!data?.updateProduct) return;
        onUpdate(cache, { data }, context);
        cache.modify({
          id: cache.identify(product),
          fields: {
            name: (): string => data.updateProduct.name,
          },
        });
      },
    });
    onClose();
  };

  return (
    <Modal isOpen={!!product} setIsOpen={onClose}>
      {product && (
        <Stack as="form" direction="column" gap={3} onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader
            icon={<Presentation width={24} color={colors.blue[600]} />}
            iconBgColor={colors.blue[50]}
            title={
              <Typography fontSize="xl" fontWeight="medium">
                Update Product
              </Typography>
            }
          />
          <Stack direction="column" paddingY={2}>
            <ControlledInput
              control={control}
              id={nameId}
              name="productName"
              label="Product Name"
              rules={{ required: 'Please provide a name' }}
            />

            <ModalActions hasTopBorder={false}>
              <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" rounded="regular" size="base" type="submit" disabled={loading}>
                Update Product
              </Button>
            </ModalActions>
          </Stack>
        </Stack>
      )}
    </Modal>
  );
};
